// src/pages/Home/container/CreativeVisionShowcase.tsx
import { useState, useEffect } from 'react';
import { 
  Sparkles, 
  Palette, 
  Camera, 
  Music,
  Pen,
  Film,
  Zap,
  Heart
} from 'lucide-react';

// Random Floating Images
const RandomFloatingImages = () => {
  const images = [
    {
      src: "https://images.unsplash.com/photo-1612544409025-e1f6a56c1152?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OHx8ZmlsbW1ha2luZ3xlbnwwfHwwfHx8MA%3D%3D",
      alt: "Creative Writing",
      position: { left: "20%", top: "18%" },
      delay: 0,
      size: "w-28 h-28 md:w-32 md:h-32"
    },
    {
      src: "https://images.unsplash.com/photo-1574375927938-d5a98e8ffe85?w=150&h=150&fit=crop&crop=center",
      alt: "cinema Production",
      position: { left: "70%", top: "22%" },
      delay: 1.5,
      size: "w-30 h-30 md:w-36 md:h-36"
    },
    {
      src: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=150&h=150&fit=crop&crop=center",
      alt: "Podcast Setup",
      position: { left: "10%", top: "55%" },
      delay: 3,
      size: "w-24 h-24 md:w-28 md:h-28"
    },
    {
      src: "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=150&h=150&fit=crop&crop=center",
      alt: "Digital Art",
      position: { left: "85%", top: "60%" },
      delay: 2,
      size: "w-26 h-26 md:w-34 md:h-34"
    }
  ];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-30">
      {images.map((image, index) => (
        <div
          key={index}
          className="absolute animate-float opacity-70 hidden sm:block"
          style={{
            left: image.position.left,
            top: image.position.top,
            animationDelay: `${image.delay}s`,
            animationDuration: `${6 + index * 0.5}s`,
          }}
        >
          <div className="relative group">
            <div className={`${image.size} rounded-xl md:rounded-2xl overflow-hidden shadow-2xl border-2 border-white/30 backdrop-blur-sm hover:scale-110 transition-transform duration-300`}>
              <img 
                src={image.src} 
                alt={image.alt}
                className="w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
            {/* Subtle glow effect */}
            <div className="absolute -inset-2 bg-white/20 rounded-xl md:rounded-2xl blur-md opacity-0 group-hover:opacity-60 transition-opacity duration-300"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Floating Creative Elements (Icons Only)
const FloatingCreativeElements = () => {
  const elements = [
    { icon: Camera, color: "text-blue-400", size: "w-8 h-8", delay: 0 },
    { icon: Music, color: "text-green-400", size: "w-6 h-6", delay: 1 },
    { icon: Pen, color: "text-orange-400", size: "w-7 h-7", delay: 2 },
    { icon: Film, color: "text-amber-400", size: "w-8 h-8", delay: 0.5 },
    { icon: Palette, color: "text-yellow-400", size: "w-6 h-6", delay: 1.5 },
    { icon: Zap, color: "text-cyan-400", size: "w-5 h-5", delay: 2.5 },
  ];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {elements.map((element, index) => (
        <div
          key={index}
          className="absolute animate-float opacity-40"
          style={{
            left: `${15 + index * 15}%`,
            top: `${20 + (index % 3) * 25}%`,
            animationDelay: `${element.delay}s`,
            animationDuration: `${4 + index * 0.5}s`,
          }}
        >
          <div className="backdrop-blur-sm bg-white/5 rounded-2xl p-3 border border-white/10">
            <element.icon className={`${element.size} ${element.color}`} />
          </div>
        </div>
      ))}
    </div>
  );
};

// Morphing Gradient Blob
const MorphingBlob = ({ delay = 0 }: { delay?: number }) => {
  return (
    <div 
      className="absolute w-96 h-96 opacity-10 animate-pulse"
      style={{ animationDelay: `${delay}s` }}
    >
      <div className="w-full h-full bg-gradient-to-br from-orange-400 via-pink-500 to-cyan-400 rounded-full filter blur-3xl transform animate-morph"></div>
    </div>
  );
};

// Inspirational Quote Carousel
const InspirationalQuotes = () => {
  const [activeQuote, setActiveQuote] = useState(0);
  
  const quotes = [
    {
      text: "Every story begins with a single moment of inspiration",
      author: "Creative Vision",
      gradient: "from-orange-500 to-red-500"
    },
    {
      text: "Where imagination meets reality, magic happens",
      author: "Artistic Journey", 
      gradient: "from-blue-400 to-cyan-400"
    },
    {
      text: "Your creativity has the power to change perspectives",
      author: "storytelling",
      gradient: "from-green-400 to-teal-400"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveQuote((prev) => (prev + 1) % quotes.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="text-center max-w-4xl mx-auto">
      <div className="relative h-32 flex items-center justify-center">
        {quotes.map((quote, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-1000 ease-in-out transform ${
              index === activeQuote 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-4 scale-95'
            }`}
          >
            <blockquote className="text-2xl md:text-3xl font-light text-white leading-relaxed mb-4 drop-shadow-lg">
              "{quote.text}"
            </blockquote>
            <cite className={`text-sm bg-gradient-to-r ${quote.gradient} bg-clip-text text-transparent font-medium drop-shadow-sm`}>
              — {quote.author}
            </cite>
          </div>
        ))}
      </div>
      
      {/* Quote indicators */}
      <div className="flex justify-center space-x-2 mt-8">
        {quotes.map((_, index) => (
          <div
            key={index}
            className={`h-1 rounded-full transition-all duration-500 ${
              index === activeQuote ? 'w-12 bg-orange-400 shadow-lg shadow-orange-400/50' : 'w-3 bg-gray-600'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// Artistic Grid Display
const ArtisticGrid = () => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  
  const artPieces = [
    { 
      gradient: "from-orange-500 via-pink-500 to-orange-600",
      title: "Originals",
      subtitle: "Imagination Unleashed"
    },
    { 
      gradient: "from-blue-500 via-cyan-500 to-blue-600", 
      title: "Cinema",
      subtitle: "Visual Production"
    },
    { 
      gradient: "from-green-500 via-emerald-500 to-teal-600",
      title: "Podcasts", 
      subtitle: "Voices of Inspiration"
    },
    { 
      gradient: "from-orange-500 via-red-500 to-red-600",
      title: "Animation",
      subtitle: "Motion & Magic"
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
      {artPieces.map((piece, index) => (
        <div
          key={index}
          className="group relative"
          onMouseEnter={() => setHoveredIndex(index)}
          onMouseLeave={() => setHoveredIndex(null)}
        >
          <div className="relative aspect-square rounded-3xl overflow-hidden">
            {/* Animated background */}
            <div className={`
              absolute inset-0 bg-gradient-to-br ${piece.gradient} 
              transition-all duration-700 transform
              ${hoveredIndex === index ? 'scale-110 rotate-2' : 'scale-100 rotate-0'}
            `}>
              {/* Animated particles */}
              <div className="absolute inset-0 opacity-30">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-2 h-2 bg-white rounded-full animate-pulse"
                    style={{
                      left: `${20 + i * 15}%`,
                      top: `${15 + (i % 3) * 25}%`,
                      animationDelay: `${i * 0.3}s`,
                      animationDuration: `${2 + i * 0.2}s`,
                    }}
                  />
                ))}
              </div>
              
              {/* Content overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                <div className={`
                  transition-all duration-500 transform text-center
                  ${hoveredIndex === index ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-90'}
                `}>
                  <h4 className="text-white font-bold text-lg mb-1 drop-shadow-lg">{piece.title}</h4>
                  <p className="text-white/90 text-sm drop-shadow-md">{piece.subtitle}</p>
                </div>
              </div>
            </div>
            
            {/* Glow effect */}
            <div className={`
              absolute -inset-1 bg-gradient-to-br ${piece.gradient} 
              rounded-3xl blur-lg opacity-0 transition-opacity duration-300
              ${hoveredIndex === index ? 'opacity-40' : 'opacity-0'}
            `} />
          </div>
        </div>
      ))}
    </div>
  );
};

// Main Creative Vision Showcase Component
const CreativeVisionShowcase = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('creative-vision-showcase');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      id="creative-vision-showcase" 
      className="relative py-24 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 overflow-hidden"
    >
      {/* Background Blobs - Reduced opacity */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10">
          <MorphingBlob delay={0} />
        </div>
        <div className="absolute top-1/4 right-0">
          <MorphingBlob delay={2} />
        </div>
        <div className="absolute bottom-1/4 left-0">
          <MorphingBlob delay={4} />
        </div>
      </div>
      
      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0 bg-slate-950/40"></div>

      <FloatingCreativeElements />
      <RandomFloatingImages />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Content */}
        <div className="text-center mb-20">
          <div className={`
            transform transition-all duration-1200 ease-out
            ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}
          `}>
            {/* Sparkle icon animation */}
            <div className="relative inline-block mb-8">
              <Sparkles className="w-16 h-16 text-yellow-400 mx-auto animate-pulse" />
              <div className="absolute -inset-4">
                <Sparkles className="w-6 h-6 text-orange-400 absolute -top-2 -left-2 animate-ping" />
                <Sparkles className="w-4 h-4 text-amber-400 absolute -bottom-2 -right-2 animate-ping" style={{ animationDelay: '1s' }} />
              </div>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-bold mb-8 leading-tight drop-shadow-lg">
              <span className="bg-gradient-to-r from-orange-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent animate-gradient drop-shadow-sm">
                Where Creativity
              </span>
              <br />
              <span className="font-display bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent animate-gradient drop-shadow-sm" style={{ animationDelay: '1s' }}>
                Comes Alive
              </span>
            </h2>
            
            <div className="w-32 h-1 bg-gradient-to-r from-orange-500 to-red-500 mx-auto mb-12 rounded-full shadow-lg shadow-orange-500/50"></div>
          </div>

          {/* Inspirational Quotes */}
          <div className={`
            transform transition-all duration-1200 ease-out
            ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}
          `} style={{ transitionDelay: '200ms' }}>
            <InspirationalQuotes />
          </div>
        </div>

        {/* Artistic Grid */}
        <div className={`
          transform transition-all duration-1200 ease-out mb-20
          ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}
        `} style={{ transitionDelay: '400ms' }}>
          <ArtisticGrid />
        </div>

        {/* Bottom artistic element */}
        <div className={`
          text-center transform transition-all duration-1200 ease-out
          ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}
        `} style={{ transitionDelay: '600ms' }}>
          <div className="relative inline-block">
            <Heart className="w-12 h-12 text-red-400 mx-auto animate-pulse" />
            <div className="absolute inset-0 animate-ping">
              <Heart className="w-12 h-12 text-amber-400 opacity-20" />
            </div>
          </div>
          <p className="text-gray-300 text-lg mt-4 max-w-2xl mx-auto leading-relaxed drop-shadow-lg">
            Every piece of content tells a story. Every story touches a heart. 
            Every heart connects to something greater.
          </p>
        </div>
      </div>

      {/* CSS for animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes float {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            25% {
              transform: translateY(-10px) rotate(2deg);
            }
            50% {
              transform: translateY(-20px) rotate(0deg);
            }
            75% {
              transform: translateY(-5px) rotate(-1deg);
            }
          }
          
          @keyframes morph {
            0%, 100% {
              transform: rotate(0deg) scale(1) skewX(0deg);
            }
            25% {
              transform: rotate(5deg) scale(1.1) skewX(2deg);
            }
            50% {
              transform: rotate(-3deg) scale(0.9) skewX(-1deg);
            }
            75% {
              transform: rotate(2deg) scale(1.05) skewX(1deg);
            }
          }
          
          @keyframes gradient {
            0% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
            100% {
              background-position: 0% 50%;
            }
          }
          
          .animate-float {
            animation: float 6s ease-in-out infinite;
          }
          
          .animate-morph {
            animation: morph 8s ease-in-out infinite;
          }
          
          .animate-gradient {
            background-size: 200% 200%;
            animation: gradient 4s ease infinite;
          }
        `
      }} />
    </section>
  );
};

export default CreativeVisionShowcase;